import { Injectable } from "@angular/core";
import { ForumApiService } from "@core/services/api/forum/forum-api.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { BehaviorSubject, Observable, Subject, of } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from "rxjs/operators";

@Injectable({
  providedIn: "root"
})
export class TitleGenerationService {
  // Minimum content length to trigger title generation
  public static readonly MIN_CONTENT_LENGTH = 50;

  // Debounce time for content changes (1000ms)
  private readonly _DEBOUNCE_TIME = 1000;

  // Rate limit for API calls (5 seconds)
  private readonly _RATE_LIMIT = 5000;

  // Cache for generated titles
  private readonly _titleCache = new Map<string, string>();

  // Flag to track if we're currently fetching a title
  private readonly _isGenerating$ = new BehaviorSubject<boolean>(false);

  // Input for content changes
  private readonly _contentChange$ = new Subject<string>();

  // Track when the last API call was made
  private _lastApiCallTime = 0;

  // Subject to emit automatically generated titles
  private readonly _autoGeneratedTitle$ = new Subject<string>();

  // Flag to globally enable/disable auto-generation
  private _autoGenerationEnabled = false;

  constructor(
    private readonly forumApiService: ForumApiService,
    private readonly utilsService: UtilsService
  ) {
    // Set up the content change pipeline with debounce and filtering
    this._contentChange$
      .pipe(
        debounceTime(this._DEBOUNCE_TIME),
        distinctUntilChanged(),
        filter(content => this._shouldGenerateTitle(content)),
        switchMap(content => this._generateTitle(content))
      )
      .subscribe(title => {
        if (title) {
          this._autoGeneratedTitle$.next(title);
        }
      });
  }

  /**
   * Get a behavior subject that tracks whether title generation is in progress
   */
  get isGenerating$(): BehaviorSubject<boolean> {
    return this._isGenerating$;
  }

  /**
   * Get an observable that emits when an automatic title has been generated
   */
  get autoGeneratedTitle$(): Observable<string> {
    return this._autoGeneratedTitle$.asObservable();
  }

  /**
   * Process content changes and trigger title generation if appropriate
   * @param content The forum post content
   * @param autoGenerationEnabled Whether auto-generation is enabled
   */
  processContentChange(content: string, autoGenerationEnabled: boolean): void {
    // Update the auto-generation flag
    this._autoGenerationEnabled = autoGenerationEnabled;

    // Only process content changes if auto-generation is enabled
    if (autoGenerationEnabled) {
      this._contentChange$.next(content);
    }
  }

  /**
   * Generate a title immediately (manual trigger)
   * @param content The forum post content
   * @returns Observable with the generated title or null if generation fails
   */
  generateTitleManually(content: string): Observable<string | null> {
    if (!content || content.length < TitleGenerationService.MIN_CONTENT_LENGTH) {
      return of(null);
    }

    return this._generateTitle(content);
  }

  /**
   * Clear the title cache
   */
  clearCache(): void {
    this._titleCache.clear();
  }

  /**
   * Check if title generation should proceed based on content and rate limiting
   */
  private _shouldGenerateTitle(content: string): boolean {
    // First check if auto-generation is enabled
    if (!this._autoGenerationEnabled) {
      return false;
    }

    // Check content length
    if (!content || content.length < TitleGenerationService.MIN_CONTENT_LENGTH) {
      return false;
    }

    // Check if we already have a cached result
    if (this._titleCache.has(content)) {
      return false;
    }

    // Apply rate limiting
    const now = Date.now();
    if (now - this._lastApiCallTime < this._RATE_LIMIT) {
      return false;
    }

    return true;
  }

  /**
   * Generate a title from the content
   */
  private _generateTitle(content: string): Observable<string | null> {
    // Check cache first
    if (this._titleCache.has(content)) {
      return of(this._titleCache.get(content) || null);
    }

    // Update rate limiting timestamp and loading state
    this._lastApiCallTime = Date.now();
    this._isGenerating$.next(true);

    // Make the API call
    return this.forumApiService.generateTopicTitle(content).pipe(
      tap(response => {
        // Cache the result
        if (response && response.title) {
          this._titleCache.set(content, response.title);
        }
        this._isGenerating$.next(false);
      }),
      catchError(error => {
        console.error("Error generating title:", error);
        this._isGenerating$.next(false);
        return of(null);
      }),
      map(response => (response ? response.title : null))
    );
  }
}
